import { MOBILE_BREAKPOINT_NAME } from 'layout-data-lib/GridLayoutDataTree/constants';
import { isMaxContentWidth } from 'layout-data-lib/GridLayoutDataTree/typenameUtils';
export const PEGBOARD_TRACK_SHIFT_OFFSET_FACTOR = 0.38;

/**
 * Calculates the offset of a pegboard item from the top-left corner of the pegboard.
 * @param cellSize - The size of a cell.
 * @param gap - The gap between cells.
 * @param targetIndex - The index of the target item.
 * @param scaleFactor - The scale factor of the item.
 * @returns The offset of the target item.
 */
export function getPegboardItemOffset(cellSize, gap, targetIndex, scaleFactor = 1) {
  // Since targetIndex on a pegboard is 1-based, we need to subtract by 1 since
  // the offset is the space between the item and the start of the pegboard.
  const index = targetIndex - 1;
  const gapOffset = index * gap * scaleFactor;
  const cellOffset = index * cellSize;
  return cellOffset + gapOffset;
}

/**
 * Calculates a dimension of a pegboard item along a single track.
 * @param cellSize - The size of a cell.
 * @param gap - The gap between cells.
 * @param spanned - The number of cells spanned by the item.
 * @param scaleFactor - The scale factor of the item.
 * @returns The dimension of the item.
 */
export function getPegboardItemDimension(cellSize, gap, spanned, scaleFactor = 1) {
  // Since gap is between items, we need to subtract spanned by 1. We also need
  // to apply the scale factor since we get gap from the layout which doesn't
  // factor in scale.
  const totalGap = gap * (spanned - 1) * scaleFactor;
  const totalSize = spanned * cellSize;
  return totalSize + totalGap;
}

/**
 * Rounds the given value to the nearest 100th decimal place
 * @param value number to round
 * @returns value rounded to nearest 100th decimal
 */
function roundToNearestHundredthDecimal(value) {
  return parseFloat(value.toFixed(2));
}

/**
 * Calculates the number of rows or columns an element on a pegboard should span.
 * @param elementSize - The height or width of the element.
 * @param cellSize - The size of a row or column in the pegboard.
 * @param gap - The size of the gap between cells.
 * @returns The number of rows or columns the element should span on the pegboard.
 */
export function getPegboardElementComputedSpanning({
  elementSize,
  cellSize,
  gap,
  maxSpan
}) {
  if (elementSize <= cellSize) return 1;
  const elementSizeAfterFirstCell = elementSize - cellSize;
  const roundedCellsSpanned = roundToNearestHundredthDecimal(elementSizeAfterFirstCell / (cellSize + gap));
  const computedSpanning = 1 + Math.ceil(roundedCellsSpanned);
  if (maxSpan) {
    return Math.min(computedSpanning, maxSpan);
  }
  return computedSpanning;
}

/**
 * Computes the row or column start of a given element in a pegboard
 * @param elementOffset The distance of the element from the top of left of the pegboard
 * @param cellSize The size of a row or column
 * @param gap The size of the gap between rows or columns
 * @returns The estimated row or column start of an element in a pegboard
 */
export function getPegboardElementComputedCellStart({
  elementOffset,
  cellSize,
  gap
}) {
  const cellStep = cellSize + gap;
  if (cellStep === 0) return 1;
  const roundedCellStart = roundToNearestHundredthDecimal(elementOffset / cellStep);
  return Math.ceil(roundedCellStart) + 1;
}

/**
 * Computes the number of rows a pegboard should have based on its height
 * @param pegboardHeight The height of the pegboard
 * @param rowSize The size of a row in the pegboard
 * @param rowGap The size of the gap between rows
 * @returns The number of rows the pegboard should have
 */
export function getPegboardComputedNumRows({
  pegboardHeight,
  rowSize,
  rowGap
}) {
  const rowStep = rowSize + rowGap;
  if (pegboardHeight < rowSize || rowStep === 0) {
    return 1;
  }
  const pegboardHeightAfterFirstRow = pegboardHeight - rowSize;
  const roundedPegboardRows = roundToNearestHundredthDecimal(pegboardHeightAfterFirstRow / rowStep);
  return 1 + Math.ceil(roundedPegboardRows);
}

/**
 * Converts a pixel delta to a row delta based on row size and gap.
 * @param pixelDelta - The pixel distance to convert.
 * @param rowSize - The size of a single row.
 * @param rowGap - The gap between rows.
 * @returns The number of rows corresponding to the pixel delta, rounded to the nearest integer.
 */
export function calculatePegboardRowDelta({
  pixelDelta,
  rowSize,
  rowGap
}) {
  const rowStep = rowSize + rowGap;
  if (rowStep === 0) return 0;
  return Math.round(pixelDelta / rowStep);
}

/**
 * Calculates a new row count clamped to a minimum value.
 * @param originalRowCount - The current number of rows.
 * @param deltaRows - The number of rows to add or remove.
 * @param minRowCount - The minimum allowed row count.
 * @returns The new row count, no less than minRowCount.
 */
export function calculateClampedRowCount({
  originalRowCount,
  deltaRows,
  minRowCount
}) {
  return Math.max(minRowCount, originalRowCount + deltaRows);
}

/**
 * Calculates the clamped position for pasting a pegboard item.
 * Applies an offset of 1 to a base position while ensuring the item stays within grid bounds.
 * @param rowStart - The starting row to offset from.
 * @param columnStart - The starting column to offset from.
 * @param pegboardRows - The total number of rows in the pegboard.
 * @param pegboardColumns - The total number of columns in the pegboard.
 * @param rowsSpanned - The number of rows spanned by the item.
 * @param columnsSpanned - The number of columns spanned by the item.
 * @returns The clamped row and column start position.
 */
export function getPegboardPastePosition({
  rowStart,
  columnStart,
  pegboardRows,
  pegboardColumns,
  rowsSpanned,
  columnsSpanned
}) {
  const maxValidRow = Math.max(1, pegboardRows - rowsSpanned + 1);
  const maxValidCol = Math.max(1, pegboardColumns - columnsSpanned + 1);
  return {
    rowStart: Math.max(1, Math.min(rowStart + 1, maxValidRow)),
    columnStart: Math.max(1, Math.min(columnStart + 1, maxValidCol))
  };
}

/**
 * Calculates the effective columns spanned for centering an element on a pegboard.
 * For an element to be centered on a pegboard, the element's column span and the total
 * number of columns must have the same parity (both odd or both even). If they differ,
 * the column span is bumped up by 1 so that (columns - effectiveColumnsSpanned) is even
 * and dividing by 2 yields a whole-number column start.
 * @param dragItemColumnsSpanned - The number of columns spanned by the dragged item.
 * @param columns - The total number of columns in the pegboard.
 * @returns The effective columns spanned to use when centering.
 */
export function getCenterOnPegboardEffectiveColumnsSpanned(dragItemColumnsSpanned, columns) {
  return dragItemColumnsSpanned % 2 !== columns % 2 ? dragItemColumnsSpanned + 1 : dragItemColumnsSpanned;
}
export const PEGBOARD_ROW_MULTIPLIER = 0.02;
export const MOBILE_BREAKPOINT_ROW_SIZE = 32;

/**
 * Calculates the size of a row in a pegboard.
 * @param breakpoint - The active breakpoint.
 * @param containerWidth - The current width of the container.
 * @param scale - The scale of the item.
 * @returns The size of the row.
 */
export const getPegboardRowSize = (breakpoint, containerWidth, scale = 1) => {
  if (breakpoint === MOBILE_BREAKPOINT_NAME) {
    return scale * MOBILE_BREAKPOINT_ROW_SIZE;
  }
  return scale * containerWidth * PEGBOARD_ROW_MULTIPLIER;
};

/**
 * Computes the value for a single track based on the provided cartesian coordinates in the corresponding direction. Assumes that the grid tracks are of uniform size and gap.
 */
export function computeTargetedPegboardTrack({
  coordinateValue,
  gridOffset,
  trackCount,
  cellSize,
  gap,
  ignoreMaxTrackCount = false,
  shiftCoordinateOffset = false
}) {
  // The boundary (first and last) cells have half of the gap size of the middle cells.
  const boundaryCellSize = cellSize + gap / 2;
  const middleCellSize = cellSize + gap;
  let firstTrackRelativePos = coordinateValue - gridOffset;
  let middleTrackRelativePos = firstTrackRelativePos;
  if (shiftCoordinateOffset) {
    firstTrackRelativePos += boundaryCellSize * PEGBOARD_TRACK_SHIFT_OFFSET_FACTOR;
    middleTrackRelativePos += middleCellSize * PEGBOARD_TRACK_SHIFT_OFFSET_FACTOR;
  }
  if (firstTrackRelativePos <= boundaryCellSize) {
    return 1;
  }
  const posAfterFirst = middleTrackRelativePos - boundaryCellSize;
  const index = 1 + Math.ceil(posAfterFirst / middleCellSize);
  return ignoreMaxTrackCount ? index : Math.min(index, trackCount);
}

/**
 * Returns true if the given x position falls within the center snap zone of the pegboard.
 * The center snap zone spans one column track (columnSize + columnGap) on each side of
 * the pegboard's horizontal midpoint. When the drag item's column span would require
 * parity coercion (odd span on even-column grid or vice versa), the zone radius is halved
 * to allow placing content to either side of center without triggering coercion.
 */
export function isPegboardCenterZone(pegboardNodePosition, xPosition, dragItemColumnsSpanned) {
  const {
    left,
    width,
    columnSize,
    columnGap,
    columns
  } = pegboardNodePosition;
  const midpoint = left + width / 2;
  const fullRadius = columnSize + columnGap / 2;
  const needsCoercion = dragItemColumnsSpanned !== undefined && dragItemColumnsSpanned % 2 !== columns % 2;
  const zoneRadius = needsCoercion ? fullRadius / 2 : fullRadius;
  return xPosition >= midpoint - zoneRadius && xPosition <= midpoint + zoneRadius;
}

/**
 * Computes the row and column at the given cartesian coordinate position on the page. The provided `cartesianCoordinates` value should
 * be derived from the browser, such as a mouse event or the persisted drag preview position.
 */
export function computePegboardTracksAtCoordinates(pegboardNodePosition, cartesianCoordinates, extraOptions) {
  const {
    top,
    left,
    columns,
    rowSize,
    columnSize,
    rowGap,
    columnGap,
    height
  } = pegboardNodePosition;
  const computedPegboardRows = getPegboardComputedNumRows({
    pegboardHeight: height,
    rowSize,
    rowGap
  });
  const column = computeTargetedPegboardTrack({
    coordinateValue: cartesianCoordinates.x,
    gridOffset: left,
    trackCount: columns,
    cellSize: columnSize,
    gap: columnGap
  });
  const row = computeTargetedPegboardTrack({
    coordinateValue: cartesianCoordinates.y,
    gridOffset: top,
    trackCount: computedPegboardRows,
    cellSize: rowSize,
    gap: rowGap,
    ignoreMaxTrackCount: extraOptions === null || extraOptions === void 0 ? void 0 : extraOptions.ignoreMaxRowsCount
  });
  return {
    row,
    column
  };
}

/**
 * Computes the mouse coordinate clamped to the pegboard bounds
 *
 * @param pegboardLowerBound Lower bound of pegboard
 * @param pegboardUpperBound Upper bound of pegboard
 * @param mouseCoord Current client mouse coordinate
 *
 * @returns Mouse coordinate clamped between pegboard upper and lower bounds
 */
export const getPegboardClampedMouseCoord = ({
  pegboardLowerBound,
  pegboardUpperBound,
  mouseCoord
}) => {
  return Math.max(pegboardLowerBound, Math.floor(Math.min(pegboardUpperBound, mouseCoord)));
};
export const DEFAULT_PADDING = 32;
export const DEFAULT_MAX_CONTENT_WIDTH = 1296;

/**
 * Calculates the computed pegboard container width
 * @param breakpoint
 * @param viewportWidthBreakpointMap Map of breakpoint to viewport width
 * @param sectionNode Pegboard parent section node
 * @returns
 */
export const getPegboardContainerWidth = (breakpoint, viewportWidthBreakpointMap, sectionNode) => {
  var _styles$padding$left$, _styles$padding, _styles$padding$right, _styles$padding2, _styles$margin$left$v, _styles$margin, _styles$margin$right$, _styles$margin2;
  const viewportWidth = viewportWidthBreakpointMap[breakpoint];
  if (!viewportWidth) {
    throw new Error('No viewport width for breakpoint');
  }
  const styles = sectionNode.getBreakpointStyles(breakpoint);
  const fullContentWidth = viewportWidth - ((_styles$padding$left$ = (_styles$padding = styles.padding) === null || _styles$padding === void 0 || (_styles$padding = _styles$padding.left) === null || _styles$padding === void 0 ? void 0 : _styles$padding.value) !== null && _styles$padding$left$ !== void 0 ? _styles$padding$left$ : DEFAULT_PADDING) - ((_styles$padding$right = (_styles$padding2 = styles.padding) === null || _styles$padding2 === void 0 || (_styles$padding2 = _styles$padding2.right) === null || _styles$padding2 === void 0 ? void 0 : _styles$padding2.value) !== null && _styles$padding$right !== void 0 ? _styles$padding$right : DEFAULT_PADDING) - ((_styles$margin$left$v = (_styles$margin = styles.margin) === null || _styles$margin === void 0 || (_styles$margin = _styles$margin.left) === null || _styles$margin === void 0 ? void 0 : _styles$margin.value) !== null && _styles$margin$left$v !== void 0 ? _styles$margin$left$v : 0) - ((_styles$margin$right$ = (_styles$margin2 = styles.margin) === null || _styles$margin2 === void 0 || (_styles$margin2 = _styles$margin2.right) === null || _styles$margin2 === void 0 ? void 0 : _styles$margin2.value) !== null && _styles$margin$right$ !== void 0 ? _styles$margin$right$ : 0);
  let maxContentWidth;
  if (styles.contentWidth && isMaxContentWidth(styles.contentWidth)) {
    maxContentWidth = styles.contentWidth.size.value;
  } else if (!styles.contentWidth) {
    maxContentWidth = DEFAULT_MAX_CONTENT_WIDTH;
  }
  const containerWidth = Math.min(maxContentWidth || fullContentWidth, fullContentWidth);
  return containerWidth;
};
export function computeMultiSelectionGridAreas(itemAreas) {
  const entries = Object.entries(itemAreas);
  if (entries.length === 0) {
    return {
      itemBoundingBoxes: {},
      groupBoundingBox: null
    };
  }
  let minRow = Infinity;
  let minCol = Infinity;
  let maxRowEnd = -Infinity;
  let maxColEnd = -Infinity;
  for (const [, {
    rowTop,
    rowBottom,
    columnLeft,
    columnRight
  }] of entries) {
    minRow = Math.min(minRow, rowTop);
    minCol = Math.min(minCol, columnLeft);
    maxRowEnd = Math.max(maxRowEnd, rowBottom);
    maxColEnd = Math.max(maxColEnd, columnRight);
  }
  const groupBoundingBox = {
    rowTop: minRow,
    rowBottom: maxRowEnd,
    columnLeft: minCol,
    columnRight: maxColEnd
  };
  const itemBoundingBoxes = {};
  for (const [id, box] of entries) {
    itemBoundingBoxes[id] = Object.assign({}, box, {
      rowOffset: box.rowTop - minRow,
      columnOffset: box.columnLeft - minCol
    });
  }
  return {
    itemBoundingBoxes,
    groupBoundingBox
  };
}
export function isCoordinateWithinBounds(coordinate, bounds) {
  return coordinate.x >= bounds.left && coordinate.x <= bounds.right && coordinate.y >= bounds.top && coordinate.y <= bounds.bottom;
}
export function areBoundsIntersecting(bounds1, bounds2) {
  return bounds1.left < bounds2.right && bounds1.right > bounds2.left && bounds1.top < bounds2.bottom && bounds1.bottom > bounds2.top;
}